import { useCallback, useEffect } from 'react';

import VirtualProvisioningRedirectNotification from '@perpay-web/fintech/components/base/Notifications/VirtualProvisioningRedirectNotification';
import CardCreditLimitIncreaseNotificationContainer from '@perpay-web/fintech/components/containers/CardCreditLimitIncreaseNotificationContainer';
import CardRewardsNotificationContainer from '@perpay-web/fintech/components/containers/CardRewardsNotificationContainer';
import { NotificationType } from '@perpay-web/fintech/reducers/entities/notifications';

import PerpayPlusNotification from './PerpayPlusNotification';
import PhoExistingUserNotification from './PhoExistingUserNotification';

const knownNotificationTypes = [
    NotificationType.PHO_EXISTING_USER,
    NotificationType.MP_PHO_EXISTING_USER,
    NotificationType.PERPAY_PLUS_TYPE,
    NotificationType.CARD_CREDIT_LIMIT_INCREASE,
    NotificationType.CARD_REWARDS,
    NotificationType.VIRTUAL_PROVISIONING,
];

const typesWithoutAutoAcknowledge = [NotificationType.VIRTUAL_PROVISIONING];

const Notifications = ({ notifications, onAcknowledge }) => {
    // We only render one notification per login, so get the first one that we recognize.
    const [notification] = notifications.filter((n) =>
        knownNotificationTypes.includes(n.type),
    );

    useEffect(() => {
        // If we do not recognize the notification, early out
        if (!notification) {
            return;
        }

        if (!typesWithoutAutoAcknowledge.includes(notification.type)) {
            onAcknowledge(notification.uuid);
        }
    }, [notification, onAcknowledge]);

    const uuid = notification && notification.uuid;
    const acknowledgeWithUuid = useCallback(() => {
        if (!uuid) {
            return;
        }

        onAcknowledge(uuid);
    }, [uuid, onAcknowledge]);

    // If there are no notifications, do not render anything.
    if (!notification) {
        return null;
    }

    // If there are notifications, render the first one.
    // If we don't recognize it, render nothing.
    switch (notification.type) {
        case NotificationType.PERPAY_PLUS_TYPE:
            return <PerpayPlusNotification />;
        case NotificationType.CARD_REWARDS:
            return (
                <CardRewardsNotificationContainer
                    cardRewardsAmount={notification.payload.rewardsAmount}
                />
            );
        case NotificationType.CARD_CREDIT_LIMIT_INCREASE:
            return (
                <CardCreditLimitIncreaseNotificationContainer
                    newCreditLimitAmount={notification.payload.newCreditLimit}
                />
            );
        case NotificationType.PHO_EXISTING_USER:
            return <PhoExistingUserNotification />;
        case NotificationType.MP_PHO_EXISTING_USER:
            return <PhoExistingUserNotification isMarketplace />;
        case NotificationType.VIRTUAL_PROVISIONING:
            return (
                <VirtualProvisioningRedirectNotification
                    onNavigate={acknowledgeWithUuid}
                />
            );
        default:
            // This shouldn't happen because of the filter above
            throw Error('unrecognized notification');
    }
};

export default Notifications;
